import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const CACHE_KEY = "mirabella_photos_cache_v1";

// Shared in-memory cache so multiple components don't each refetch.
let memCache = null;
let inFlight = null;
const subscribers = new Set();

function notify(photos) {
  memCache = photos;
  subscribers.forEach((fn) => fn(photos));
}

async function fetchPhotos() {
  if (inFlight) return inFlight;
  inFlight = api
    .get("/photos")
    .then((r) => {
      const photos = r.data || [];
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(photos));
      } catch (_) {
        // sessionStorage may fail if the payload is enormous; that's fine.
      }
      notify(photos);
      return photos;
    })
    .catch(() => {
      notify(memCache || []);
      return memCache || [];
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

function readCache() {
  if (memCache) return memCache;
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      memCache = JSON.parse(cached);
      return memCache;
    }
  } catch (_) {
    // ignore
  }
  return null;
}

/**
 * Shared photos cache. Returns the current photo array (from session cache
 * if available — instant on repeat visits) and re-fetches in the background
 * so newly uploaded photos appear automatically.
 */
export function usePhotos() {
  const [photos, setPhotos] = useState(() => readCache());

  useEffect(() => {
    subscribers.add(setPhotos);
    fetchPhotos();
    return () => {
      subscribers.delete(setPhotos);
    };
  }, []);

  return {
    photos: photos || [],
    loaded: photos !== null,
  };
}
